import { Utilities } from "../../../utils/utilities";
import CollectionLoader from "../General/CollectionLoader";
import { ReadAccess } from "../General/AccessData";
import { FirebaseData } from "../General/FirebaseData";
import { LocalData } from "../General/LocalData";
import { TranslationData } from "../General/TranslationData";
import { UserData } from "../User/UserData";
import { EventAttendeeData } from "./EventAttendeeData";
import { z } from "zod";

const TRawEventNotificationSettings = z.object({
  onCreate: z.object({
    platforms: z.object({
      mobile: z.boolean(),
      email: z.boolean(),
    }),
  }),
});
export type IRawEventNotificationSettings = z.infer<typeof TRawEventNotificationSettings>;

export class EventData extends FirebaseData {
  title: TranslationData = new TranslationData();
  description: TranslationData = new TranslationData();

  startDate?: Date;
  endDate?: Date;
  price?: string;
  registrationDeadline?: Date;
  maxAttendees?: number;
  attendeesVisible?: boolean;
  attendingCount: number = 0;
  notificationSettings?: IRawEventNotificationSettings;

  readAccess: ReadAccess = {};
  attendees: CollectionLoader<EventAttendeeData>;

  constructor(id: string, collection: string, parent?: FirebaseData) {
    super(id, collection, parent);
    this.attendees = new CollectionLoader("attendees", this, EventAttendeeData);
  }

  setup(data: any): void {
    this.title.setup(data.title);
    this.description.setup(data.description);

    this.startDate = Utilities.getDateFromTimestamp(data.startDate);
    this.endDate = Utilities.getDateFromTimestamp(data.endDate);

    this.price = typeof data.price === "string" ? data.price : undefined;
    this.registrationDeadline = Utilities.getDateFromTimestamp(data.registrationDeadline);
    this.maxAttendees = typeof data.maxAttendees === "number" ? data.maxAttendees : undefined;
    this.attendeesVisible = typeof data.attendeesVisible === "boolean" ? data.attendeesVisible : undefined;
    this.attendingCount = typeof data.attendingCount === "number" ? data.attendingCount : 0;

    this.readAccess = Utilities.getReadAccess(data.readAccess);

    const notificationSettings = TRawEventNotificationSettings.safeParse(data.notificationSettings);
    this.notificationSettings = notificationSettings.success ? notificationSettings.data : undefined;

    super.setup(data);
  }

  canSeeAttendees(): boolean {
    return this.attendeesVisible || (this.readAccess.properties?.some((p) => p.checkForLandlordRoles(LocalData.currentUser)) ?? false);
  }

  getVisibleAttendees(): EventAttendeeData[] {
    if (this.canSeeAttendees()) {
      return this.attendees.documents;
    }
    return this.attendees.documents.filter((doc) => doc.user === LocalData.currentUser);
  }

  getAttendingUsers(): UserData[] {
    const attendees = this.getVisibleAttendees();

    let attendingUsers: UserData[] = [];
    for (const attendee of attendees) {
      if (attendee.user) {
        attendingUsers.push(attendee.user);
      }
    }
    attendingUsers = attendingUsers.sort((a, b) => a.name.localeCompare(b.name));

    return attendingUsers;
  }
}
